import {
  explainHiddenThreadMessage,
  mapThreadResponse,
  type ChatMessage,
  type ThreadApiMessage,
} from '@teo/widget-shared';
import { fetchThread } from '../api/fetch-thread.js';
import { logRealtime } from '../realtime/realtime-logger.js';
import { logSync, summarizeChatMessage } from './sync-logger.js';

function summarizeRawThreadMessage(message: ThreadApiMessage): Record<string, unknown> {
  return {
    id: message.id,
    role: message.message_role,
    type: message.type,
    source_id: message.source_id,
    message: message.message?.slice(0, 120),
    rawType: message.raw_message?.type,
    payload: message.raw_message?.payload?.slice(0, 120),
  };
}

export type ThreadLoadResult = {
  messages: ChatMessage[];
  managerName: string | null;
};

/** GET /api/thread/{id} → sorted visitor-facing messages + thread metadata. */
export async function loadThreadMessages(
  apiBase: string,
  threadId: number | string,
): Promise<ThreadLoadResult> {
  const thread = await fetchThread(threadId, apiBase);
  const messages = mapThreadResponse(thread);

  const hidden = thread.messages.flatMap((row) => {
    const reason = explainHiddenThreadMessage(row);
    if (!reason) return [];
    return [{ ...summarizeRawThreadMessage(row), reason }];
  });

  logRealtime('thread', 'GET /api/thread mapped', {
    threadId,
    rawCount: thread.messages.length,
    visibleCount: messages.length,
    hiddenCount: hidden.length,
    lastMessage: messages.at(-1) ? summarizeChatMessage(messages.at(-1)!) : null,
  });

  logSync('thread', 'GET /api/thread/{id} mapped', {
    threadId,
    rawCount: thread.messages.length,
    visibleCount: messages.length,
    hidden,
    visible: messages.map(summarizeChatMessage),
  });

  if (hidden.length > 0) {
    logSync(
      'thread',
      'hidden API messages (may explain missing links after reload)',
      hidden,
      'warn',
    );
  }

  return {
    messages,
    managerName: thread.company?.ai_manager_name?.trim() || null,
  };
}
